import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, Clock } from "lucide-react";

const COL_CHAPES = [
  { to: "/chapes/chape-traditionnelle", label: "Chape traditionnelle" },
  { to: "/chapes/chape-allegee", label: "Chape allégée" },
  { to: "/chapes/beton-dur", label: "Béton dur" },
  { to: "/chapes/rampe-acces", label: "Rampe d'accès garage" },
];

const COL_ISOLATIONS = [
  { to: "/isolations/isolation-thermique", label: "Isolation thermique" },
  { to: "/isolations/isolation-phonique", label: "Isolation phonique" },
];

const COL_SOLUTIONS = [
  { to: "/solutions/renovation-sols", label: "Rénovation des sols" },
  { to: "/solutions/sechage-rapide", label: "Séchage rapide" },
  { to: "/solutions/sols-trafic", label: "Sols à trafic intense" },
];

const COL_SOCIETE = [
  { to: "/a-propos", label: "À propos" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/contact", label: "Contact" },
  { to: "/devis", label: "Demander un devis" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#1A2D7E] text-[#F4F4F0]">
      {/* Top CTA */}
      <div className="px-6 md:px-12 lg:px-24 py-16 md:py-20 border-b border-[#2D3F8E]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold mb-4">
              / Contact
            </div>
            <h3 className="font-display font-bold uppercase tracking-tighter text-3xl md:text-4xl lg:text-5xl leading-[0.95]">
              Parlons de votre <span className="text-[#F08722]">projet de sol.</span>
            </h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/devis"
              data-testid="footer-devis-cta"
              className="inline-flex items-center gap-3 bg-[#F08722] text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-[#1A2D7E] transition-colors"
            >
              Demander un devis <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Columns */}
      <div className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand + contact */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 bg-[#F08722] flex items-center justify-center">
                <span className="font-display font-black text-white text-xl">E</span>
              </div>
              <div className="font-display font-black uppercase tracking-tight leading-[0.95]">
                <div className="text-base">EURO</div>
                <div className="text-base text-[#F08722]">CHAPE</div>
              </div>
            </div>
            <p className="text-sm font-light text-[#E6E6E0] leading-relaxed max-w-sm">
              Spécialiste de la chape, du béton et de l'isolation des sols
              en Alsace et Territoire de Belfort.
            </p>
            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#F08722] flex-shrink-0" />
                <a
                  data-testid="footer-phone"
                  href="tel:+33767945459"
                  className="link-underline"
                >
                  07 67 94 54 59
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#F08722] flex-shrink-0" />
                <a
                  data-testid="footer-email"
                  href="mailto:eurochape68@gmail.com"
                  className="link-underline break-all"
                >
                  eurochape68@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#F08722] flex-shrink-0" />
                <span data-testid="footer-address">
                  1 rue de Habsheim
                  <br />
                  68110 Illzach
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-[#F08722] flex-shrink-0" />
                <span>Lun – Ven : 8h – 18h</span>
              </li>
            </ul>
          </div>

          <FooterColumn title="Nos chapes" items={COL_CHAPES} />
          <FooterColumn title="Nos isolations" items={COL_ISOLATIONS} />
          <FooterColumn title="Nos solutions" items={COL_SOLUTIONS} />
          <FooterColumn title="La société" items={COL_SOCIETE} />
        </div>
      </div>

      {/* Bottom strip */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-t border-[#2D3F8E] flex flex-col md:flex-row justify-between gap-3 text-xs text-[#9A9A95]">
        <span>© {new Date().getFullYear()} Euro Chape — Tous droits réservés.</span>
        <span>Illzach · Alsace · Territoire de Belfort · France</span>
      </div>

      {/* Giant logo word */}
      <div
        aria-hidden
        className="px-6 md:px-12 lg:px-24 pb-10 font-display font-black uppercase leading-[0.85] tracking-tighter text-[#142368] select-none text-[22vw] md:text-[16vw] overflow-hidden"
      >
        EURO·CHAPE
      </div>
    </footer>
  );
}

function FooterColumn({ title, items }) {
  return (
    <div className="lg:col-span-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold mb-6">
        {title}
      </div>
      <ul className="space-y-3 text-sm">
        {items.map((it) => (
          <li key={it.to}>
            <Link to={it.to} className="link-underline text-[#E6E6E0] hover:text-white">
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
